/**
 * Prerequisite Check — LDG-based prerequisite gating
 *
 * Checks whether a family's hard prerequisites are met
 * based on the Learning Dependency Graph (LDG).
 *
 * Extracted from adaptiveEngine.ts in O-2 (god-file split).
 */

import { getTierRangeForGroup } from '../../content'
// Deep import (NOT the governance barrel) — keeps the mastery hot-path from
// pulling the entire governance pipeline into the bundle eagerly. See
// selectionConfig.ts comment for the boot-perf rationale.
import { getLDG } from '../../governance/ldg'
import { GroupId } from '../../config/systemConfig'
import { FamilyMasteryState, getFamilyMasteryKey } from '../familyMasteryEngine'
import {
  type InferredMasteryEntry,
  getInferredMastery,
  isInferredPassing,
  resolveMasterySignal,
} from '../inferredMastery'
import { V11_CONFIG, debugLog } from './selectionConfig'

/**
 * Rich prerequisite status (FG3 §F0.D — enrichedPrerequisite model).
 *
 * Replaces the implicit `boolean` contract with an EXPLICIT union. A boolean
 * loses information (why did it pass? was it bypassed? is it cold-start?); the
 * union makes the reason a first-class, observable value consumed jointly by
 * selection logic, the teacher dashboard, and analytics.
 *
 * Anti-bandaid principle #2: no booleans where an enum can stand.
 *
 * NOTE: 'inferred-passed' and 'soft-pending' are reserved for FG3 (S4 —
 * Inferred Mastery via Placement + probe injection). They are defined now so
 * the contract is stable; the current evaluator does not yet produce them.
 */
export type PrereqStatus =
  | { kind: 'no-prerequisites' }
  | { kind: 'hard-passed'; via: 'mastery' }
  | { kind: 'bypassed'; via: 'group-tier'; prereqId: string }
  | { kind: 'inferred-passed'; via: 'placement'; confidence: number }
  | {
      kind: 'soft-pending'
      lastSignal: 'no-data' | 'recent-fail'
      prereqId: string
    }
  | { kind: 'blocked'; prereqId: string; mastery: number; threshold: number }

export interface PrerequisiteEvaluation {
  /** True when nothing blocks progression (any non-'blocked' overall status). */
  met: boolean
  /** The decisive overall status (first blocker, else the strongest pass). */
  status: PrereqStatus
  /** Per-prerequisite breakdown for dashboards + analytics. */
  details: PrereqStatus[]
}

/**
 * Evaluate a family's prerequisites and return a RICH status breakdown.
 * Same topology and gating decision as `arePrerequisitesMet`, but the reason
 * for every pass/block is explicit and observable.
 *
 * FG3 (S5): optional `inferredMastery` store. When supplied, intra-band
 * prereqs lacking observed attempts consult the inferred layer:
 *   - inferred score >= threshold AND confidence >= MIN_CONFIDENCE_FOR_PASS
 *     → `inferred-passed` (lets the learner through with a soft signal)
 *   - inferred missing OR insufficient
 *     → `soft-pending` instead of `blocked` (the selector can inject a probe)
 *
 * Backward-compatible: when `inferredMastery` is `undefined` the evaluator
 * behaves identically to pre-S5 (intra-band cold-start → `blocked`).
 */
export function evaluatePrerequisites(
  familyId: string,
  familyMastery: Record<string, FamilyMasteryState>,
  selectedGroup: GroupId,
  threshold: number = V11_CONFIG.LDG_PREREQUISITE_MASTERY,
  inferredMastery?: Record<string, InferredMasteryEntry>,
): PrerequisiteEvaluation {
  const graph = getLDG()
  const node = graph.nodes.get(familyId)
  if (!node || node.prerequisites.length === 0) {
    return {
      met: true,
      status: { kind: 'no-prerequisites' },
      details: [{ kind: 'no-prerequisites' }],
    }
  }

  const groupTierRange = getTierRangeForGroup(selectedGroup)
  const details: PrereqStatus[] = []

  for (const prereqId of node.prerequisites) {
    const edge = graph.edges.find(
      (e) => e.from === prereqId && e.to === familyId,
    )
    // Only enforce hard prerequisites (soft are recommendations)
    if (edge && edge.type === 'soft') continue

    // v11.7: Auto-pass prerequisites from lower groups.
    const prereqNode = graph.nodes.get(prereqId)
    if (prereqNode && prereqNode.tier < groupTierRange.min) {
      debugLog(
        `   ✅ Prerequisite auto-passed (lower group): ${prereqId} (tier ${prereqNode.tier} < group ${selectedGroup} min ${groupTierRange.min})`,
      )
      details.push({ kind: 'bypassed', via: 'group-tier', prereqId })
      continue
    }

    const masteryKey = getFamilyMasteryKey(prereqId, selectedGroup)
    const prereqState = familyMastery[masteryKey]
    const inferredEntry = inferredMastery
      ? getInferredMastery(inferredMastery, prereqId, selectedGroup)
      : undefined

    // FG3 §S5 — resolve the mastery signal across observed + inferred layers.
    // Observed wins when present; inferred is the fallback for cold-start.
    const signal = resolveMasterySignal(prereqState, inferredEntry)

    if (signal.kind === 'observed') {
      if (signal.score < threshold) {
        debugLog(
          `   🔒 Prerequisite not met: ${prereqId} (observed mastery: ${signal.score}, threshold: ${threshold}) blocks ${familyId}`,
        )
        const blocked: PrereqStatus = {
          kind: 'blocked',
          prereqId,
          mastery: signal.score,
          threshold,
        }
        details.push(blocked)
        // First blocker decides the overall gate.
        return { met: false, status: blocked, details }
      }
      details.push({ kind: 'hard-passed', via: 'mastery' })
      continue
    }

    if (signal.kind === 'inferred') {
      if (isInferredPassing(signal.entry, threshold)) {
        debugLog(
          `   ✨ Prerequisite inferred-passed: ${prereqId} (inferred score ${signal.score}, confidence ${signal.entry.confidence}, source ${signal.entry.source})`,
        )
        details.push({
          kind: 'inferred-passed',
          via: 'placement',
          confidence: signal.entry.confidence,
        })
        continue
      }
      // Inferred exists but isn't strong enough — soft-pending so the
      // selector can inject a probe rather than hard-blocking.
      debugLog(
        `   ⏳ Prerequisite soft-pending (inferred insufficient): ${prereqId} (score ${signal.score}, conf ${signal.entry.confidence})`,
      )
      details.push({
        kind: 'soft-pending',
        lastSignal: 'no-data',
        prereqId,
      })
      // Soft-pending does NOT block the gate when inferredMastery is
      // explicitly supplied — the caller opted into the soft model.
      continue
    }

    // signal.kind === 'none' — no observed attempts AND no inferred entry.
    if (inferredMastery) {
      // Caller opted into the soft model. Emit soft-pending so the
      // selector can choose to probe. Does NOT block.
      debugLog(
        `   ⏳ Prerequisite soft-pending (cold-start, no inferred): ${prereqId}`,
      )
      details.push({
        kind: 'soft-pending',
        lastSignal: 'no-data',
        prereqId,
      })
      continue
    }

    // Pre-S5 behaviour: caller did not opt into inferred. Cold-start
    // blocks the same way as before — no regression possible.
    debugLog(
      `   🔒 Prerequisite not met: ${prereqId} (no mastery, threshold: ${threshold}) blocks ${familyId}`,
    )
    const blocked: PrereqStatus = {
      kind: 'blocked',
      prereqId,
      mastery: 0,
      threshold,
    }
    details.push(blocked)
    return { met: false, status: blocked, details }
  }

  // No blockers — pick a representative "passed" status for the overall view.
  // Priority: hard-passed > inferred-passed > bypassed > soft-pending > no-prerequisites.
  const overall: PrereqStatus = details.find((d) => d.kind === 'hard-passed') ??
    details.find((d) => d.kind === 'inferred-passed') ??
    details.find((d) => d.kind === 'bypassed') ??
    details.find((d) => d.kind === 'soft-pending') ??
    details[0] ?? { kind: 'no-prerequisites' }

  return { met: true, status: overall, details }
}

/**
 * Check if a family's hard prerequisites are met based on mastery state.
 * Returns true if all hard prerequisites have mastery >= threshold.
 *
 * RESOLVES: CONFLICT-01 (LDG Exists But Is Not Enforced)
 *
 * v11.4: Default threshold raised from STRUGGLING_THRESHOLD (40) to
 * LDG_PREREQUISITE_MASTERY (60) for tighter prerequisite enforcement.
 *
 * FG3 §F0.D: thin boolean wrapper over `evaluatePrerequisites`.
 * FG3 §S5: optional `inferredMastery` parameter — when supplied, cold-start
 * prereqs are treated as soft (no block) rather than hard-blocked. Omit it
 * to preserve pre-S5 behaviour.
 */
export function arePrerequisitesMet(
  familyId: string,
  familyMastery: Record<string, FamilyMasteryState>,
  selectedGroup: GroupId,
  threshold: number = V11_CONFIG.LDG_PREREQUISITE_MASTERY,
  inferredMastery?: Record<string, InferredMasteryEntry>,
): boolean {
  return evaluatePrerequisites(
    familyId,
    familyMastery,
    selectedGroup,
    threshold,
    inferredMastery,
  ).met
}
